/*
 * @Author: wangweixin
 * @Date: 2017-11-28 15:30:27
 * @Last Modified by: wangweixin
 * @Last Modified time: 2021-03-04 19:22:39
 */
import React, { useRef } from 'react';
import ReactDom from 'react-dom';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { merge, get } from 'lodash';
import Button from '../Button';
import Item from '../Item';
import Icon from '../Icon';
import Loading from '../Loading';
import closeIcon from './images/close-modal.svg';

const baseModalStyle = {
  overlay: {
    backgroundColor: 'rgba(27, 29, 31, 0.5)',
    zIndex: 10,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  content: {
    bottom: 'auto',
    border: 'none',
    background: '#FFF',
    overflow: 'hidden',
    borderRadius: '2px',
    outline: 'none',
    padding: '0px',
    width: '700px',
    zIndex: 10,
    maxHeight: window.innerHeight - 100
  }
};

// triky way to generate the doc
// https://react-styleguidist.js.org/docs/thirdparties/#how-styleguidist-works
export function Modal(props) {
  const {
    title,
    children,
    footer,
    handleCancel,
    handleEnsure,
    btnCancelTxt,
    btnEnsureTxt,
    style,
    closable,
    width,
    className,
    isOpen,
    size,
    containerUtil,
    btnMini,
    loading,
    loadingDesc,
    ...other
  } = props;
  const classes = classNames(className, 'modal-content');
  const ref = useRef();

  containerUtil.getContainer = () => {
    return ref.current;
  };

  if (!isOpen) {
    return null;
  }
  const styles = merge({}, baseModalStyle, style);

  const sizeWidth = {
    mini: 300,
    normal: 400,
    large: 550
  }[size];

  const resultWidth = width || sizeWidth;

  styles.content.width = get(style, 'content.width') || resultWidth;

  return (
    <div className="modal" style={styles.overlay} onClick={handleCancel}>
      <div
        className={`${classes}`}
        style={styles.content}
        {...other}
        onClick={e => e.stopPropagation()}
      >
        <Item show={title}>
          <div className="modal-header">
            {title}
            <Item show={closable}>
              <Icon
                mini
                className="close-icon"
                onClick={handleCancel}
                link={closeIcon}
              />
            </Item>
          </div>
        </Item>
        <div
          className="modal-body"
          ref={ref}
          style={{ maxHeight: window.innerHeight - 202 }}
        >
          {children}
        </div>
        <div className="modal-footer">
          {!footer ? (
            <div className="footer-btn-wrap">
              <Button type="white" mini={btnMini} onClick={handleCancel}>
                {btnCancelTxt}
              </Button>
              <Button type="primary" mini={btnMini} onClick={handleEnsure}>
                {btnEnsureTxt}
              </Button>
            </div>
          ) : (
            footer
          )}
        </div>
        <Item show={loading}>
          <div className="loading-wrap">
            <Loading type="box">
              <span>{loadingDesc}</span>
            </Loading>
          </div>
        </Item>
      </div>
    </div>
  );
}

export default props => {
  return ReactDom.createPortal(<Modal {...props} />, document.body);
};

Modal.defaultProps = {
  closable: true,
  btnCancelTxt: '取消',
  btnEnsureTxt: '确定',
  btnMini: true,
  size: 'normal',
  containerUtil: {},
  loading: false,
  loadingDesc: '提交中...'
};
Modal.propTypes = {
  /** 弹框标题，可以是string, 也可以是节点 */
  title: PropTypes.any,
  /** 自定义底部按钮，假设进行自定义，需要手动为按钮绑定回调事件 */
  footer: PropTypes.any,
  /** 是否在loading状态，loading状态会展示遮罩 */
  loading: PropTypes.bool,
  /** loading状态的描述 */
  loadingDesc: PropTypes.any,
  /** 是否展示关闭按钮 */
  closable: PropTypes.bool,
  /** 控制弹窗的展示状态 */
  isOpen: PropTypes.bool,
  /** 取消的回调事件 */
  handleCancel: PropTypes.func,
  /** 确定的回调事件 */
  handleEnsure: PropTypes.func,
  /** 修改取消按钮的内容 */
  btnCancelTxt: PropTypes.any,
  /** 修改确定按钮的内容 */
  btnEnsureTxt: PropTypes.any,
  /** 按钮是否是mini形式 */
  btnMini: PropTypes.bool,
  /** 自定义宽度 */
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  /** 三种大小的弹框, 300px, 450px, 550px */
  size: PropTypes.oneOf(['mini', 'normal', 'large']),
  /**
   * 样式设定
   * {
   *   overlay: 蒙版,
   *   content: 内容
   * }
   */
  style: PropTypes.object,
  /**传入一个对象，会默认注入getContainer方法 */
  containerUtil: PropTypes.object
};
